/**
 * Security Issues API Service
 *
 * Service functions for interacting with the security issues API endpoints.
 */

import { apiClient } from "./api";
import type {
  SecurityIssue,
  IssueStatus,
  SeverityLevel,
  IssueType,
  TraceStep,
  AttackFlowStep,
  RisksContainer,
  SuggestedFix,
} from "@/types/security";

const API_PREFIX = "/api/v1/security-issues";

/**
 * Backend response type (matches SecurityIssueRead schema)
 */
export interface SecurityIssueBackend {
  id: number;
  user_id: number;
  token_id: number | null;
  issue_type: string;
  failed_scanners: string[];
  severity: string;
  status: string;
  title: string;
  description: string;
  llm_guard_results: Record<string, unknown>;
  gemini_verification: Record<string, unknown> | null;
  risk_score: number;
  detected_at: string;
  verified_at: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  trace_ids: number[];
  // Extended detail fields (Phase 2)
  execution_traces: TraceStep[] | null;
  attack_flow: AttackFlowStep[] | null;
  risks: RisksContainer | null;
  suggested_fix: SuggestedFix | null;
}

// Note: TraceStep, AttackFlowStep, RisksContainer, SuggestedFix types are imported from @/types/security

/**
 * Paginated response from backend
 */
export interface PaginatedResponse<T> {
  data: T[];
  total_count: number;
  has_more: boolean;
  page: number;
  items_per_page: number;
}

/**
 * Filters for listing security issues
 */
export interface SecurityIssueFilters {
  page?: number;
  items_per_page?: number;
  severity?: SeverityLevel;
  status?: IssueStatus;
  issue_type?: IssueType;
}

/**
 * Update data for security issues
 */
export interface SecurityIssueUpdateData {
  status?: IssueStatus;
  description?: string;
  resolved_at?: string;
}

/**
 * Generate display ID from numeric ID (e.g., 1 -> "SEC-001")
 */
function generateDisplayId(id: number): string {
  return `SEC-${id.toString().padStart(3, "0")}`;
}

/**
 * Extract detected content from llm_guard_results if available
 */
function extractDetectedContent(results: Record<string, unknown>): string | undefined {
  // Try to extract from input_scan or output_scan
  const inputScan = results.input_scan as Record<string, unknown> | undefined;
  const outputScan = results.output_scan as Record<string, unknown> | undefined;

  if (inputScan?.detected_content) {
    return String(inputScan.detected_content);
  }
  if (outputScan?.detected_content) {
    return String(outputScan.detected_content);
  }

  // Try to get from top level
  if (results.detected_content) {
    return String(results.detected_content);
  }

  return undefined;
}

/**
 * Extract sanitized content from llm_guard_results if available
 */
function extractSanitizedContent(results: Record<string, unknown>): string | undefined {
  const inputScan = results.input_scan as Record<string, unknown> | undefined;
  const outputScan = results.output_scan as Record<string, unknown> | undefined;

  if (inputScan?.sanitized_content) {
    return String(inputScan.sanitized_content);
  }
  if (outputScan?.sanitized_content) {
    return String(outputScan.sanitized_content);
  }

  if (results.sanitized_content) {
    return String(results.sanitized_content);
  }

  return undefined;
}

/**
 * Transform backend response to frontend SecurityIssue type
 */
function transformToSecurityIssue(backend: SecurityIssueBackend): SecurityIssue {
  return {
    id: generateDisplayId(backend.id),
    timestamp: backend.detected_at,
    type: backend.issue_type as IssueType,
    severity: backend.severity as SeverityLevel,
    status: backend.status as IssueStatus,
    title: backend.title,
    description: backend.description,
    traceId: backend.trace_ids.length > 0 ? `trace_${backend.trace_ids[0]}` : "",
    scanner: backend.failed_scanners.length > 0 ? backend.failed_scanners[0] : backend.issue_type,
    riskScore: backend.risk_score,
    detectedContent: extractDetectedContent(backend.llm_guard_results),
    sanitizedContent: extractSanitizedContent(backend.llm_guard_results),
    metadata: backend.llm_guard_results as { [key: string]: unknown },
    // Extended detail fields (Phase 2)
    traces: backend.execution_traces as TraceStep[] | undefined,
    attackFlow: backend.attack_flow as AttackFlowStep[] | undefined,
    risks: backend.risks as RisksContainer | undefined,
    suggestedFix: backend.suggested_fix as SuggestedFix | undefined,
  };
}

/**
 * List security issues for the current user with optional filters.
 */
export async function listSecurityIssues(
  filters?: SecurityIssueFilters
): Promise<PaginatedResponse<SecurityIssue>> {
  const params = new URLSearchParams();

  if (filters?.page) params.append("page", filters.page.toString());
  if (filters?.items_per_page) params.append("items_per_page", filters.items_per_page.toString());
  if (filters?.severity) params.append("severity", filters.severity);
  if (filters?.status) params.append("status", filters.status);
  if (filters?.issue_type) params.append("issue_type", filters.issue_type);

  const queryString = params.toString();
  const url = `${API_PREFIX}${queryString ? `?${queryString}` : ""}`;

  const response = await apiClient.get<PaginatedResponse<SecurityIssueBackend>>(url);

  return {
    ...response,
    data: response.data.map(transformToSecurityIssue),
  };
}

/**
 * Get a single security issue by ID.
 * @param id - The numeric ID of the issue (not the display ID)
 */
export async function getSecurityIssue(id: number): Promise<SecurityIssue> {
  const response = await apiClient.get<SecurityIssueBackend>(`${API_PREFIX}/${id}`);
  return transformToSecurityIssue(response);
}

/**
 * Parse display ID to numeric ID (e.g., "SEC-001" -> 1)
 */
export function parseDisplayId(displayId: string): number {
  const match = displayId.match(/SEC-(\d+)/i);
  if (match) {
    return parseInt(match[1], 10);
  }
  // If it's already a number string, parse it
  const num = parseInt(displayId, 10);
  if (!isNaN(num)) {
    return num;
  }
  throw new Error(`Invalid issue ID format: ${displayId}`);
}

/**
 * Get a single security issue by display ID.
 * @param displayId - The display ID (e.g., "SEC-001") or numeric ID
 */
export async function getSecurityIssueByDisplayId(displayId: string): Promise<SecurityIssue> {
  const id = parseDisplayId(displayId);
  return getSecurityIssue(id);
}

/**
 * Update a security issue.
 * @param id - The numeric ID of the issue
 * @param data - The update data
 */
export async function updateSecurityIssue(
  id: number,
  data: SecurityIssueUpdateData
): Promise<{ message: string }> {
  return apiClient.patch<{ message: string }>(`${API_PREFIX}/${id}`, data);
}

/**
 * Delete a security issue (soft delete).
 * @param id - The numeric ID of the issue
 */
export async function deleteSecurityIssue(id: number): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`${API_PREFIX}/${id}`);
}

/**
 * Stats response types
 */
export interface SeverityCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

export interface SeverityTrends {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface StatusTrendItem {
  date: string;
  opened: number;
  resolved: number;
  in_review: number;
}

export interface AgeDistributionItem {
  label: string;
  count: number;
  percentage: number;
  color: string;
}

export interface OpenIssueAge {
  average_days: number;
  distribution: AgeDistributionItem[];
}

export interface SecurityIssueStats {
  severity_counts: SeverityCounts;
  severity_trends: SeverityTrends;
  status_trend: StatusTrendItem[];
  open_issue_age: OpenIssueAge;
}

/**
 * Get aggregated statistics for security issues.
 * @param days - Number of days for trend analysis (default: 30)
 */
export async function getSecurityIssueStats(days: number = 30): Promise<SecurityIssueStats> {
  const params = new URLSearchParams();
  params.append("days", days.toString());
  return apiClient.get<SecurityIssueStats>(`${API_PREFIX}/stats?${params.toString()}`);
}
